import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePermission } from '../hooks/usePermission';
import Popup from './Model/Popup';
import * as Authservices from '../Services/services';
import {
    PERMISSION_GROUPS,
    TOGGLEABLE_SECTIONS,
    SUPER_ADMIN_ROLE,
    buildEmptyPermissions,
    countGrantedPermissions,
    canModuleBeDeleted,
} from '../config/permissions';

const ACTION_LABELS = { view: 'View', edit: 'Edit', delete: 'Delete' };

const isSuperAdminRoleRow = (role) =>
    role?.name === SUPER_ADMIN_ROLE || role?.is_super_admin === true;

const roleDisplayName = (role) => role?.label || role?.display_name || role?.name || 'Untitled role';

/**
 * Normalises whatever the roles endpoint returns into a dense
 * { module: { action: bool } } map covering the 21 sections.
 */
const normalisePermissions = (raw) => {
    const perms = buildEmptyPermissions();
    if (!raw) return perms;

    // Shape A: already a flat map — { series: { view: true, edit: true } }
    if (!Array.isArray(raw)) {
        Object.entries(raw).forEach(([module, actions]) => {
            if (!perms[module] || !actions || typeof actions !== 'object') return;
            Object.entries(actions).forEach(([action, value]) => {
                if (action in perms[module]) perms[module][action] = value === true || value === 1;
            });
        });
        return perms;
    }

    // Shape B: a list of permission rows — [{ module, action }] or [{ name: 'series:edit' }]
    raw.forEach((row) => {
        const module = row?.module || row?.resource || String(row?.name || '').split(/[:.]/)[0];
        const action = row?.action || String(row?.name || '').split(/[:.]/)[1];
        if (perms[module] && action in perms[module]) perms[module][action] = true;
    });
    return perms;
};

/** Builds `module:action` → permission id from the backend permission catalogue. */
const buildPermissionIndex = (rows) => {
    const index = {};
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const id = row?.id ?? row?.permission_id;
        if (id === undefined || id === null) return;
        const module = row?.module || row?.resource || String(row?.name || '').split(/[:.]/)[0];
        const action = row?.action || String(row?.name || '').split(/[:.]/)[1];
        if (module && action) index[`${module}:${action}`] = id;
    });
    return index;
};

const RoleManagement = () => {
    const { canView, canEdit, isSuperAdmin } = usePermission();

    const [roles, setRoles] = useState([]);
    const [permissionIndex, setPermissionIndex] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newRole, setNewRole] = useState({ name: '', description: '' });
    const [creating, setCreating] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [blockedDelete, setBlockedDelete] = useState(null);

    const readOnly = !canEdit('role_management');

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        try {
            const [rolesRes, catalogRes] = await Promise.allSettled([
                Authservices.getRoles(),
                Authservices.getPermissionCatalog(),
            ]);

            if (rolesRes.status === 'fulfilled') {
                const list = rolesRes.value?.data?.responseDetails || rolesRes.value?.data?.data || [];
                const rows = (Array.isArray(list) ? list : list?.rows || []).map((role) => ({
                    ...role,
                    permissions: normalisePermissions(role.permissions),
                    admin_count: role.admin_count ?? role.admins_count ?? role.users_count ?? 0,
                }));
                setRoles(rows);
            } else {
                toast.error('Failed to load roles');
                console.error('[RoleManagement] roles error:', rolesRes.reason);
            }

            if (catalogRes.status === 'fulfilled') {
                const list = catalogRes.value?.data?.responseDetails || catalogRes.value?.data?.data || [];
                setPermissionIndex(buildPermissionIndex(Array.isArray(list) ? list : list?.rows));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRoles(); }, [fetchRoles]);

    const activeRole = useMemo(
        () => roles.find((r) => r.id === selectedRoleId) || null,
        [roles, selectedRoleId],
    );

    const activeIsSuperAdmin = isSuperAdminRoleRow(activeRole);
    const gridLocked = readOnly || activeIsSuperAdmin;

    const selectRole = (role) => {
        setSelectedRoleId(role.id);
        setDraft(JSON.parse(JSON.stringify(role.permissions)));
    };

    const isDirty = useMemo(() => {
        if (!activeRole || !draft) return false;
        return JSON.stringify(draft) !== JSON.stringify(activeRole.permissions);
    }, [draft, activeRole]);

    const togglePermission = (module, action) => {
        if (gridLocked) return;
        setDraft((prev) => {
            const next = { ...prev, [module]: { ...prev[module], [action]: !prev[module]?.[action] } };

            // Losing View means losing the section entirely, so the actions that
            // only make sense inside it come off with it.
            if (action === 'view' && !next[module].view) {
                Object.keys(next[module]).forEach((a) => { next[module][a] = false; });
            }
            // Granting Edit or Delete implies being able to see the section.
            if (action !== 'view' && next[module][action]) {
                next[module].view = true;
            }
            return next;
        });
    };

    const setAllForRole = (value) => {
        if (gridLocked) return;
        setDraft(() => {
            const next = buildEmptyPermissions();
            if (value) {
                TOGGLEABLE_SECTIONS.forEach((s) => {
                    s.actions.forEach((a) => { next[s.key][a] = true; });
                });
            }
            return next;
        });
    };

    const handlePublish = async () => {
        if (!activeRole || gridLocked) return;
        setSaving(true);
        try {
            // Prefer permission_ids (the documented contract). Fall back to the
            // nested map when the permission catalogue isn't reachable.
            const permissionIds = [];
            let complete = Object.keys(permissionIndex).length > 0;
            if (complete) {
                TOGGLEABLE_SECTIONS.forEach((section) => {
                    section.actions.forEach((action) => {
                        if (!draft?.[section.key]?.[action]) return;
                        const id = permissionIndex[`${section.key}:${action}`];
                        if (id === undefined) complete = false;
                        else permissionIds.push(id);
                    });
                });
            }

            const payload = complete
                ? { permission_ids: permissionIds, permissions: draft }
                : { permissions: draft };

            const res = await Authservices.updateRolePermissions(activeRole.id, payload);
            if (res?.status >= 200 && res.status < 300) {
                toast.success(`Permissions published for "${roleDisplayName(activeRole)}"`);
                setRoles((prev) => prev.map((r) => (r.id === activeRole.id ? { ...r, permissions: draft } : r)));
                if (activeRole.admin_count > 0) {
                    toast.info(
                        `${activeRole.admin_count} signed-in admin${activeRole.admin_count === 1 ? '' : 's'} on this role ` +
                        'will be signed out and must log in again under the new permissions.',
                    );
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.responseMessage || 'Failed to publish permissions');
        } finally {
            setSaving(false);
        }
    };

    // Every exit from the Create Role dialog goes through here. The modal is
    // conditionally rendered, so closing it unmounts the inputs — but newRole
    // lives on this component and survives, and reopening rebinds the inputs to
    // the retained draft. Only the success path used to clear it, which is why
    // Cancel and the cross left the old name and description behind.
    const closeCreateModal = () => {
        setShowCreateModal(false);
        setNewRole({ name: '', description: '' });
    };

    const handleCreate = async () => {
        const name = newRole.name.trim();
        if (!name) {
            toast.error('Role name is required');
            return;
        }
        if (roles.some((r) => (r.name || '').toLowerCase() === name.toLowerCase())) {
            toast.error('A role with that name already exists');
            return;
        }
        const description = newRole.description.trim().slice(0, 100);
        setCreating(true);
        try {
            const res = await Authservices.createRole({
                name,
                description,
            });
            if (res?.status >= 200 && res.status < 300) {
                toast.success(`Role "${name}" created with no permissions — configure them below.`);
                closeCreateModal();
                await fetchRoles();
            }
        } catch (err) {
            toast.error(err?.response?.data?.responseMessage || 'Failed to create role');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            const res = await Authservices.deleteRole(confirmDelete.id);
            if (res?.status >= 200 && res.status < 300) {
                toast.success('Role deleted');
                if (selectedRoleId === confirmDelete.id) { setSelectedRoleId(null); setDraft(null); }
                setConfirmDelete(null);
                fetchRoles();
            }
        } catch (err) {
            const status = err?.response?.status;
            const body = err?.response?.data;
            // A role with members can't be deleted — say how many and where to go.
            if (status === 409) {
                setConfirmDelete(null);
                setBlockedDelete({
                    role: confirmDelete,
                    message: body?.responseMessage || body?.message,
                    count: confirmDelete.admin_count,
                });
            } else {
                toast.error(body?.responseMessage || 'Failed to delete role');
            }
        } finally {
            setDeleting(false);
        }
    };

    if (!canView('role_management')) return null;

    return (
        <section className="rbac-section section">
            <div className="rbac-header">
                <div>
                    <h4>Role Management</h4>
                    <p className="rbac-subtitle">
                        {loading
                            ? 'Loading…'
                            : 'Create roles and grant access section by section. Changes take effect immediately.'}
                    </p>
                </div>
                {!readOnly && (
                    <button className="btn btn-primary" onClick={() => setShowCreateModal(true)} disabled={loading}>
                        <i className="fas fa-plus"></i> Create Role
                    </button>
                )}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 80, color: '#8a8a9a' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: 36, marginBottom: 16 }}></i>
                    <p>Loading roles…</p>
                </div>
            ) : (
                <div className="rbac-layout">
                    <div className="rbac-roles-list">
                        {roles.length === 0 && (
                            <div className="rbac-empty-state" style={{ padding: 24 }}>
                                <p>No roles yet.</p>
                            </div>
                        )}
                        {roles.map((role) => {
                            const isSA = isSuperAdminRoleRow(role);
                            const { granted, total } = countGrantedPermissions(role.permissions);
                            const shown = isSA ? total : granted;
                            const pct = total > 0 ? Math.round((shown / total) * 100) : 0;
                            const members = role.admin_count ?? 0;
                            return (
                                <div
                                    key={role.id}
                                    className={`rbac-role-card ${selectedRoleId === role.id ? 'active' : ''}`}
                                    onClick={() => selectRole(role)}
                                >
                                    <div className="rbac-role-card-header">
                                        <div className="rbac-role-icon" style={{ background: 'rgba(248,37,129,0.13)', color: '#f82581' }}>
                                            <i className={isSA ? 'fas fa-crown' : 'fas fa-user-shield'}></i>
                                        </div>
                                        <div className="rbac-role-info">
                                            <h6>
                                                {roleDisplayName(role)}
                                                {isSA && (
                                                    <span title="System role — permanently protected" style={{ marginLeft: 6, fontSize: 10, color: '#f59e0b' }}>
                                                        <i className="fas fa-lock"></i> protected
                                                    </span>
                                                )}
                                            </h6>
                                            <span className="rbac-role-users">
                                                {members} member{members === 1 ? '' : 's'}
                                            </span>
                                        </div>
                                        {!isSA && !readOnly && (
                                            <button
                                                className="rbac-delete-btn"
                                                title={members > 0
                                                    ? `${members} admin(s) assigned — reassign them in Team Members first`
                                                    : 'Delete role'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (members > 0) {
                                                        setBlockedDelete({ role, count: members });
                                                    } else {
                                                        setConfirmDelete(role);
                                                    }
                                                }}
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        )}
                                    </div>
                                    {role.description && (
                                        <p className="rbac-role-desc" title={role.description}>{role.description}</p>
                                    )}
                                    <div className="rbac-perm-bar">
                                        <div className="rbac-perm-bar-fill" style={{ width: `${pct}%`, background: '#f82581' }}></div>
                                    </div>
                                    <span className="rbac-perm-count">
                                        {isSA ? 'Full access to every section' : `${granted}/${total} permissions`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="rbac-permissions-panel">
                        {activeRole && draft ? (
                            <>
                                <div className="rbac-panel-header">
                                    <div className="rbac-panel-title">
                                        <div className="rbac-role-icon-lg" style={{ background: 'rgba(248,37,129,0.13)', color: '#f82581' }}>
                                            <i className={activeIsSuperAdmin ? 'fas fa-crown' : 'fas fa-user-shield'}></i>
                                        </div>
                                        <div>
                                            <h5>{roleDisplayName(activeRole)} Permissions</h5>
                                            {activeRole.description && <p>{activeRole.description}</p>}
                                        </div>
                                    </div>
                                    {!gridLocked && (
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            <button className="btn btn-secondary" onClick={() => setAllForRole(false)} title="Clear every permission">
                                                Clear all
                                            </button>
                                            <button className="btn btn-secondary" onClick={() => setAllForRole(true)} title="Grant every permission">
                                                Select all
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handlePublish}
                                                disabled={saving || !isDirty}
                                                style={{ minWidth: 150 }}
                                            >
                                                {saving
                                                    ? <><i className="fas fa-spinner fa-spin"></i> Publishing…</>
                                                    : isDirty
                                                        ? <><i className="fas fa-paper-plane"></i> Publish Changes</>
                                                        : <><i className="fas fa-check"></i> Published</>}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {activeIsSuperAdmin && (
                                    <div className="rbac-notice rbac-notice-warn">
                                        <i className="fas fa-lock"></i>
                                        <span>
                                            The Super Admin role is system-defined. It has full unrestricted access and
                                            cannot be renamed, deleted, or have its permissions changed.
                                        </span>
                                    </div>
                                )}

                                {!gridLocked && isDirty && activeRole.admin_count > 0 && (
                                    <div className="rbac-notice rbac-notice-info">
                                        <i className="fas fa-bolt"></i>
                                        <span>
                                            Publishing signs out the {activeRole.admin_count} admin
                                            {activeRole.admin_count === 1 ? '' : 's'} on this role immediately — they'll
                                            log back in under the updated permissions.
                                        </span>
                                    </div>
                                )}

                                {readOnly && !activeIsSuperAdmin && (
                                    <div className="rbac-notice rbac-notice-info">
                                        <i className="fas fa-eye"></i>
                                        <span>You have view-only access to Role Management.</span>
                                    </div>
                                )}

                                <div className="rbac-grid">
                                    <div className="rbac-grid-head">
                                        <span>Section</span>
                                        <span>View</span>
                                        <span>Edit</span>
                                        <span>Delete</span>
                                    </div>

                                    {PERMISSION_GROUPS.map((group) => (
                                        <div key={group.group} className="rbac-grid-group">
                                            <div className="rbac-grid-group-title">{group.group}</div>
                                            {group.sections.map((section) => {
                                                const key = section.key;
                                                return (
                                                    <div className="rbac-grid-row" key={section.label}>
                                                        <span className="rbac-grid-label">{section.label}</span>
                                                        {['view', 'edit', 'delete'].map((action) => {
                                                            // Delete exists only on the five Content sub-modules.
                                                            if (action === 'delete' && !canModuleBeDeleted(key)) {
                                                                return <span key={action} className="rbac-grid-na">—</span>;
                                                            }
                                                            if (!section.actions.includes(action)) {
                                                                return <span key={action} className="rbac-grid-na">—</span>;
                                                            }
                                                            const on = activeIsSuperAdmin || !!draft?.[key]?.[action];
                                                            const disabled = gridLocked;
                                                            return (
                                                                <span key={action} className="rbac-grid-cell">
                                                                    <button
                                                                        type="button"
                                                                        role="switch"
                                                                        aria-checked={on}
                                                                        aria-label={`${ACTION_LABELS[action]} ${section.label}`}
                                                                        className={`rbac-toggle ${on ? 'active' : ''} ${disabled ? 'rbac-toggle-disabled' : ''}`}
                                                                        onClick={() => !disabled && togglePermission(key, action)}
                                                                        title={
                                                                            activeIsSuperAdmin ? 'Super Admin always has full access'
                                                                                : readOnly ? 'You do not have permission to edit roles'
                                                                                    : ''
                                                                        }
                                                                    >
                                                                        <span className="rbac-toggle-knob"></span>
                                                                    </button>
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="rbac-empty-state">
                                <i className="fas fa-shield-alt"></i>
                                <h5>Select a Role</h5>
                                <p>Pick a role to view and configure its permissions</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showCreateModal && (
                <Popup
                    title="Create Role"
                    icon="fas fa-user-shield"
                    maxWidth={520}
                    onClose={closeCreateModal}
                    footer={<>
                        <button className="btn btn-secondary" onClick={closeCreateModal}>Cancel</button>
                        <button className="btn" onClick={handleCreate} disabled={creating || !newRole.name.trim()}>
                            {creating ? <><i className="fas fa-spinner fa-spin"></i> Creating…</> : 'Create Role'}
                        </button>
                    </>}
                >
                    <div className="form-group">
                        <label htmlFor="role-name">Role Name</label>
                        <input
                            id="role-name" type="text" placeholder="e.g. Content Editor" autoFocus
                            value={newRole.name}
                            onChange={(e) => setNewRole((p) => ({ ...p, name: e.target.value }))}
                        />
                    </div>
                    <div className="form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label htmlFor="role-description">Description</label>
                            <span style={{ fontSize: 11, color: newRole.description.length >= 100 ? '#ef4444' : '#8a8a9a' }}>
                                {newRole.description.length}/100
                            </span>
                        </div>
                        <input
                            id="role-description" type="text" placeholder="What this role is for (max 100 chars)"
                            maxLength={100}
                            value={newRole.description}
                            onChange={(e) => setNewRole((p) => ({ ...p, description: e.target.value.slice(0, 100) }))}
                        />
                    </div>
                    <p className="rbac-hint">
                        <i className="fas fa-info-circle"></i>
                        New roles start with zero permissions. Open the role afterwards to configure and publish its access.
                    </p>
                </Popup>
            )}

            {confirmDelete && (
                <Popup
                    title={`Delete “${roleDisplayName(confirmDelete)}”?`}
                    icon="fas fa-exclamation-triangle"
                    iconColor="#ef4444"
                    maxWidth={460}
                    onClose={() => setConfirmDelete(null)}
                    footer={<>
                        <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                        <button className="btn btn-danger-solid" onClick={handleDelete} disabled={deleting}>
                            {deleting ? <><i className="fas fa-spinner fa-spin"></i> Deleting…</> : 'Delete Role'}
                        </button>
                    </>}
                >
                    <p className="rbac-modal-text">
                        No admins are assigned to this role, so it can be removed safely. This cannot be undone.
                    </p>
                </Popup>
            )}

            {blockedDelete && (
                <Popup
                    title="This role still has members"
                    icon="fas fa-users"
                    iconColor="#f59e0b"
                    maxWidth={500}
                    onClose={() => setBlockedDelete(null)}
                    footer={<>
                        <button className="btn btn-secondary" onClick={() => setBlockedDelete(null)}>Close</button>
                        <Link className="btn" to="/admin-users" onClick={() => setBlockedDelete(null)}>
                            Go to Team Members
                        </Link>
                    </>}
                >
                    <p className="rbac-modal-text">
                        {blockedDelete.message || (
                            <>
                                <strong>{blockedDelete.count} admin{blockedDelete.count === 1 ? '' : 's'}</strong>{' '}
                                {blockedDelete.count === 1 ? 'is' : 'are'} still assigned to
                                “{roleDisplayName(blockedDelete.role)}”.
                            </>
                        )}
                    </p>
                    <p className="rbac-modal-text">
                        Reassign them to a different role in <strong>Admin → Team Members</strong>, then delete this role.
                        Each reassignment signs that admin out immediately.
                    </p>
                </Popup>
            )}

            {!isSuperAdmin && !readOnly && (
                <p style={{ color: '#8a8a9a', fontSize: 12, marginTop: 16 }}>
                    <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
                    Assigning roles to team members is restricted to Super Admins.
                </p>
            )}

            <style>{`
                /* Card & description bounds and word wrapping */
                .rbac-role-card {
                    overflow: hidden;
                    min-width: 0;
                    max-width: 100%;
                    box-sizing: border-box;
                }
                .rbac-role-info {
                    min-width: 0;
                    overflow: hidden;
                }
                .rbac-role-info h6 {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    word-break: break-word;
                }
                .rbac-role-desc {
                    word-break: break-word;
                    word-wrap: break-word;
                    overflow-wrap: anywhere;
                    white-space: normal;
                    max-width: 100%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                    line-clamp: 2;
                }
                .rbac-panel-title {
                    min-width: 0;
                    flex: 1;
                }
                .rbac-panel-title > div {
                    min-width: 0;
                    flex: 1;
                }
                .rbac-panel-title h5,
                .rbac-panel-title p {
                    word-break: break-word;
                    overflow-wrap: anywhere;
                    white-space: normal;
                    max-width: 100%;
                }
                /* Title on the left, Clear/Select/Publish on the right. */
                .rbac-permissions-panel .rbac-panel-header {
                    display: flex; align-items: flex-start;
                    justify-content: space-between; gap: 16px; flex-wrap: wrap;
                }
                .rbac-permissions-panel .rbac-panel-title { min-width: 0; }
                .rbac-notice {
                    display: flex; align-items: flex-start; gap: 10px;
                    padding: 10px 14px; border-radius: 8px; font-size: 13px;
                    margin-bottom: 14px; line-height: 1.5;
                }
                .rbac-notice-warn { background: rgba(245,158,11,0.10); border: 1px solid rgba(245,158,11,0.30); color: #f59e0b; }
                .rbac-notice-info { background: rgba(99,102,241,0.10); border: 1px solid rgba(99,102,241,0.30); color: #8b90f8; }
                .rbac-grid { width: 100%; overflow-x: auto; }
                .rbac-grid-head,
                .rbac-grid-row {
                    display: grid;
                    grid-template-columns: minmax(190px, 1fr) 78px 78px 78px;
                    align-items: center; gap: 8px;
                }
                .rbac-grid-head {
                    padding: 8px 12px; font-size: 11px; letter-spacing: 0.08em;
                    text-transform: uppercase; color: #8a8a9a;
                    border-bottom: 1px solid rgba(138,138,154,0.20);
                }
                .rbac-grid-head span:not(:first-child),
                .rbac-grid-row > span:not(:first-child) { text-align: center; }
                .rbac-grid-group { margin-top: 14px; }
                .rbac-grid-group-title {
                    font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
                    color: #f82581; font-weight: 600; padding: 6px 12px;
                }
                .rbac-grid-row {
                    padding: 8px 12px; border-radius: 8px;
                }
                .rbac-grid-row:hover { background: rgba(138,138,154,0.07); }
                .rbac-grid-label { font-size: 14px; display: flex; align-items: center; gap: 8px; }
                .rbac-grid-note {
                    font-size: 10px; font-style: normal; color: #8a8a9a;
                    border: 1px solid rgba(138,138,154,0.35); border-radius: 999px; padding: 1px 7px;
                }
                .rbac-modal-text {
                    color: var(--body-text-color); font-size: 14px;
                    line-height: 1.6; margin: 0 0 14px;
                    word-break: break-word; overflow-wrap: anywhere;
                }
                .rbac-hint {
                    display: flex; align-items: flex-start; gap: 8px;
                    font-size: 12.5px; color: #8a8a9a; line-height: 1.55; margin: 0;
                }
                .btn-danger-solid {
                    background: #ef4444 !important;
                    border-color: #ef4444 !important;
                    color: #fff !important;
                }
                .rbac-grid-na { color: #55555f; font-size: 13px; }
                .rbac-grid-cell { display: flex; justify-content: center; }
                /* .rbac-toggle is styled for a div in main-style.css — reset the button chrome. */
                button.rbac-toggle { border: none; padding: 0; display: block; appearance: none; }
                .rbac-toggle-disabled { opacity: 0.55; cursor: not-allowed; }
                @media (max-width: 640px) {
                    .rbac-grid-head, .rbac-grid-row {
                        grid-template-columns: minmax(140px, 1fr) 58px 58px 58px;
                    }
                }
            `}</style>
        </section>
    );
};

export default RoleManagement;
